import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { industries, ProvenResult, industryKnowledge } from '../data/mockData';
import {
  IndustryIcon1,
  IndustryIcon2,
  IndustryIcon3,
  IndustryIcon4,
  IndustryIcon6,
  IndustryIcon5,
  IndustryIcon7,
  IndustryIcon8,
  IndustryIcon9,
} from '../components/Icons/Icons'

const IndustriesPage = () => {
  const getIcon = (iconName) => {
    const icons = {
      IndustryIcon1,
      IndustryIcon2,
      IndustryIcon3,
      IndustryIcon4,
      IndustryIcon6,
      IndustryIcon5,
      IndustryIcon7,
      IndustryIcon8,
      IndustryIcon9,
    };
    return icons[iconName] || IndustryIcon1;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in">
            <h1 className="display-lg text-white mb-6">
              A CRM Configured for How Your Industry Works
            </h1>
            <p className="body-lg mb-8">
              A bank, a clinic and a manufacturer do not sell or serve customers the same way. GrowSquare is set up for each: the sales stages, the records you keep, the approvals you need and the reports you read.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-start items-start sm:items-center">
              <Link to="/demo">
                <Button className="btn-primary glow-effect">
                  Request Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="btn-secondary">
                  Industry Expert Call
                </Button>
              </Link>
            </div>
          </div>
          <div className="relative flex justify-center">
            <img
              src='/images/industriesBanner.webp'
              alt="Team meeting where a presenter walks colleagues through ideas on sticky notes"
              loading="eager"
              className="h-[380px] w-auto max-w-full rounded-2xl object-contain shadow-2xl sm:h-[460px] lg:h-[580px]"
            />
          </div>
          </div>
        </div>
      </section>

      {/* Industries Overview */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-16">
            <Badge className="bg-green-400/10 text-green-400 mb-4">Industry Expertise</Badge>
            <h2 className="display-md text-white mb-6">
              Same Platform, Configured for Your Sector
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              Each vertical has its own buying process, compliance habits and customer data. We start from the standard GrowSquare modules and adjust them to fit.
            </p>
          </div>

          <div className="card-grid">
            {industries.map((industry,i) => {
              const IconComponent = getIcon(industry.icon);

              return (
                <Card key={i} className="feature-card hover-card">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-green-400/10 flex items-center justify-center mb-4">
                      <IconComponent className="h-6 w-6 text-green-400" />
                    </div>
                    <CardTitle className="h3 text-white">{industry.name}</CardTitle>
                    <CardDescription className="body-md">
                      {industry.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-6">
                      <h4 className="body-md font-semibold text-white mb-3">How we set it up:</h4>
                      <div className="space-y-2">
                        {industry.setupHighlights.map((solution, index) => (
                          <div key={index} className="flex items-start space-x-2">
                            <CheckCircle className="h-4 w-4 text-green-400 mt-1 flex-shrink-0" />
                            <span className="body-sm text-slate-300">{solution}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <Link to={`/industries/${industry.name}`}>
                      <Button className="btn-secondary w-full">
                        Learn More
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industry Stats */}
      <section className="section-padding section-dark">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="display-md text-white mb-6">
              {ProvenResult.title}
            </h2>
            <p className="body-lg max-w-2xl mx-auto">
              {ProvenResult.description}
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-8">
            {ProvenResult.results.map((challenge, index) => (
              <div key={index}>
                <h3 className="h3 text-green-400 mb-2">{challenge.title}</h3>
                <p className="body-sm text-slate-300">{challenge.subtitle}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Expertise */}
      <section className="section-padding">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="display-md text-white mb-6">
                CRM Know-How Across Functions
              </h2>
              <p className="body-lg mb-8">
                Our team has set up CRM for sales, marketing, service and finance teams across these sectors. We bring that experience to the configuration instead of asking you to explain CRM basics.
              </p>
              <div className="space-y-6">
                {industryKnowledge.knowledgePoints.map((challenge, index) => (
                  <div className="flex items-start space-x-4" key={index}>
                    <div className="w-10 h-10 rounded-lg bg-green-400/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    </div>
                    <div>
                      <h3 className="h3 text-white mb-2">{challenge.title}</h3>
                      <p className="body-md text-slate-300">
                        {challenge.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                {industryKnowledge.knowledgeCard.map((industry, index) => {
                  const IconComponent = getIcon(industry.icon);
                  return (
                    <Card className="feature-card" key={index}>
                      <CardContent className="pt-6 text-center">
                        <IconComponent className="h-8 w-8 text-green-400 mx-auto mb-2" />
                        <p className="body-sm font-medium text-white">{industry.title}</p>
                        {/* <p className="body-sm text-slate-400">85+ companies</p> */}
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding section-dark">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="display-md text-white mb-6">
              Want to See It for Your Sector?
            </h2>
            <p className="body-lg mb-8 max-w-2xl mx-auto">
              Book a demo and we will set up the sales stages and records for your industry before the call.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo">
                <Button className="btn-primary glow-effect">
                  Industry-Specific Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="btn-secondary">
                  Speak with Industry Expert
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
export default IndustriesPage;